import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/api/client';
import { useTenantStore } from '@/stores/tenantStore';

import { workflowKeys } from './workflowKeys';

export type WorkflowStatusTarget = 'Active' | 'Draft' | 'Archived';

interface BulkStatusArgs {
  // Each selected workflow with its CURRENT status (needed to pick the right
  // transition endpoint — there is no direct Archived→Active call).
  items: Array<{ id: string; status: string }>;
  target: WorkflowStatusTarget;
}

export interface BulkStatusResult {
  total: number;
  succeeded: number;
  failed: number;
}

// Moves many workflows to one status in a single action. Reuses the existing
// per-workflow endpoints (publish / unpublish / archive / restore) and fans them
// out with Promise.allSettled so one failure doesn't abort the rest. Archived→
// Active chains restore then publish (mirrors the single-row status picker).
export function useBulkWorkflowStatus() {
  const qc = useQueryClient();
  const tenantId = useTenantStore((s) => s.tenantId);

  return useMutation({
    mutationFn: async ({ items, target }: BulkStatusArgs): Promise<BulkStatusResult> => {
      const base = `api/v1/t/${tenantId}/workflows`;

      const applyOne = async (w: { id: string; status: string }) => {
        if (w.status === target) return; // already there — nothing to do
        if (target === 'Active') {
          if (w.status === 'Archived') await apiClient.post(`${base}/${w.id}/restore`);
          await apiClient.post(`${base}/${w.id}/publish`);
        } else if (target === 'Draft') {
          if (w.status === 'Active') await apiClient.post(`${base}/${w.id}/unpublish`);
          else if (w.status === 'Archived') await apiClient.post(`${base}/${w.id}/restore`);
        } else {
          await apiClient.post(`${base}/${w.id}/archive`);
        }
      };

      const results = await Promise.allSettled(items.map(applyOne));
      const failed = results.filter((r) => r.status === 'rejected').length;
      return { total: items.length, succeeded: items.length - failed, failed };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: workflowKeys.lists() });
    },
  });
}
